import type React from 'react';

import { Card, Link, Text } from '@change/ds-core-ui';
import { PetitionIcon } from '@change/ds-icons/PetitionIcon';
import { ShareNowIcon } from '@change/ds-icons/ShareNowIcon';
import { SpeakerIcon } from '@change/ds-icons/SpeakerIcon';
import type { Icon } from '@change/ds-icons/types';
import { Grid } from '@change/ds-layout';

import { Translate } from '@change-corgi/core-react/i18n';

import { PETITION_OPTIMIZATION_CALL_URL } from '#src/app/pages/ambassadors/landing/shared/consts';

import type { StepLink } from './hooks/useHowItWorks';
import { useHowItWorks } from './hooks/useHowItWorks';

type Step = Readonly<{
	key: string;
	icon: React.FC<Icon>;
	title: React.ReactNode;
	body: React.ReactNode;
	// Optional trailing link, rendered inline after the body. Kept as its own translation key
	// rather than an <a> embedded in the body copy, so no HTML parsing or replace callback is
	// needed to give it the design-system Link.
	link?: Readonly<{
		href: string;
		label: React.ReactNode;
	}>;
}>;

const STEPS: readonly Step[] = [
	{
		key: 'step_1',
		icon: PetitionIcon,
		title: <Translate value="corgi.ambassadors.process.step_1.title" />,
		body: <Translate value="corgi.ambassadors.process.step_1.body" />,
	},
	{
		key: 'step_2',
		icon: SpeakerIcon,
		title: <Translate value="corgi.ambassadors.process.step_2.title" />,
		body: <Translate value="corgi.ambassadors.process.step_2.body" />,
		link: {
			href: PETITION_OPTIMIZATION_CALL_URL,
			label: <Translate value="corgi.ambassadors.process.step_2.link" />,
		},
	},
	{
		key: 'step_3',
		icon: ShareNowIcon,
		title: <Translate value="corgi.ambassadors.process.step_3.title" />,
		body: <Translate value="corgi.ambassadors.process.step_3.body" />,
	},
];

const StepCard: React.FC<{ step: Step; onLinkClick: (step: StepLink) => void }> = ({ step, onLinkClick }) => {
	const StepIcon = step.icon;
	const { link } = step;

	return (
		<Card className="bg-background-base flex h-full flex-col gap-4 p-6">
			<div className="border-stroke-brand-strong bg-background-base flex size-11 items-center justify-center rounded-full border-2">
				<StepIcon size={22} className="text-fill-brand-strong" />
			</div>
			<Text as="h3" variant="heading" size="x-small">
				{step.title}
			</Text>
			{/* The link sits inside the same Text so it flows inline after the body copy. `inline`
			    overrides the DS Link's `inline-flex`, an atomic box that can't break mid-text, which
			    would drop it onto its own line. The underline is the Link's own default — no override
			    needed for it. */}
			<Text className="text-typography-weak">
				{step.body}
				{link ? (
					<>
						{' '}
						<Link
							href={link.href}
							target="_blank"
							variant="secondary"
							className="text-typography-weak inline"
							onClick={() => onLinkClick({ key: step.key, href: link.href })}
						>
							{link.label}
						</Link>
					</>
				) : null}
			</Text>
		</Card>
	);
};

export const HowItWorks: React.FC<EmptyProps> = () => {
	const { actions } = useHowItWorks();

	return (
		<section id="process" className="py-16">
			<div className="container">
				<Text as="p" size="small" fontWeight="bold" className="text-stroke-brand-strong mb-2 tracking-wide uppercase">
					<Translate value="corgi.ambassadors.process.label" />
				</Text>
				<Text as="h2" variant="heading" size="large">
					<Translate value="corgi.ambassadors.process.heading" />
				</Text>
			</div>

			<Grid className="mt-8">
				{STEPS.map((step) => (
					<div key={step.key} className="col-span-4 md:col-span-8 lg:col-span-4">
						<StepCard step={step} onLinkClick={actions.trackLinkClick} />
					</div>
				))}
			</Grid>
		</section>
	);
};
